<template>
  <div>
    <v-dialog v-model="isOpenModal" max-width="760" persistent scrollable>
    <v-card id="edit-company" class="tw-rounded-2xl">
      <!-- Header -->
      <v-card-title
        class="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b dialog-header"
      >
        <div class="tw-flex tw-items-center tw-gap-2">
          <v-icon color="#6ec39b" size="24">mdi-office-building-cog-outline</v-icon>
          <span class="tw-text-xl tw-font-bold tw-text-slate-800">แก้ไขข้อมูลบริษัท</span>
          <v-chip
            v-if="environmentBadge"
            x-small
            :color="environmentBadge.color"
            text-color="white"
          >{{ environmentBadge.label }}</v-chip>
        </div>
        <v-btn icon :disabled="loading" aria-label="ปิด" class="close-btn" @click="onClickClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="tw-px-6 tw-py-5">
        <v-form ref="form" @submit.prevent="submit">
          <!-- ───────── ข้อมูลบริษัท ───────── -->
          <div class="form-section">
            <div class="section-header">
              <v-icon color="#6ec39b" class="tw-mr-2">mdi-domain</v-icon>
              ข้อมูลบริษัท
            </div>

            <div class="tw-flex tw-items-center tw-gap-4 tw-mb-5">
              <div class="logo-uploader" @click="$refs.inputUpload.click()">
                <template v-if="companyObject.imageLogoUrl">
                  <div class="logo-preview-wrapper">
                    <img
                      v-if="typeof companyObject.imageLogoUrl !== 'string'"
                      :src="companyObject.imageLogoShow"
                      :alt="companyObject.nameTH"
                    />
                    <img
                      v-else
                      :srcset="
                        companyObject.imageLogoUrl + '=c-w320, ' +
                        companyObject.imageLogoUrl + '=c-w480 1.5x, ' +
                        companyObject.imageLogoUrl + '=c-w640 2x'
                      "
                      :src="companyObject.imageLogoUrl + '=c-w640'"
                      :alt="companyObject.nameTH"
                    />
                    <div class="logo-overlay">
                      <v-icon color="white" size="20">mdi-camera-outline</v-icon>
                      <span class="logo-overlay-text">เปลี่ยนโลโก้</span>
                    </div>
                  </div>
                </template>
                <div v-else class="logo-placeholder">
                  <v-icon color="#94a3b8" size="28">mdi-image-plus</v-icon>
                  <span class="logo-placeholder-text">อัปโหลด</span>
                </div>

                <v-progress-circular
                  v-if="uploadLogoState && uploadCompanyStatus !== 'success'"
                  class="logo-progress"
                  :rotate="-90"
                  :size="80"
                  :width="3"
                  :value="uploadLogoState"
                  color="teal"
                ></v-progress-circular>
              </div>

              <div>
                <div class="tw-font-bold tw-text-slate-800 tw-text-base">โลโก้บริษัท</div>
                <div class="tw-text-xs tw-text-slate-500 tw-mb-2">
                  รองรับ PNG / JPG กว้างอย่างน้อย 640px
                </div>
                <v-btn
                  small
                  outlined
                  color="#6ec39b"
                  class="tw-rounded-lg tw-font-semibold"
                  :disabled="loading"
                  @click="$refs.inputUpload.click()"
                >
                  <v-icon left small>mdi-upload</v-icon>
                  เลือกภาพ
                </v-btn>
                <input
                  v-show="false"
                  ref="inputUpload"
                  type="file"
                  accept="image/*"
                  @change="onFileChanged"
                />
              </div>
            </div>

            <v-row>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.nameTH"
                  outlined
                  dense
                  hide-details="auto"
                  label="ชื่อนิติบุคคล (Thai)"
                  required
                  :disabled="loading"
                  :rules="rules.inputRule"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.nameEN"
                  outlined
                  dense
                  hide-details="auto"
                  label="ชื่อนิติบุคคล (EN)"
                  required
                  :disabled="loading"
                  :rules="rules.inputRule"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-select
                  v-model="companyObject.countryCode"
                  :items="countryItems"
                  item-text="text"
                  item-value="value"
                  label="ประเทศ"
                  outlined
                  dense
                  hide-details="auto"
                  color="#6ec39b"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.taxID"
                  outlined
                  dense
                  hide-details="auto"
                  :label="taxIDLabel"
                  required
                  :disabled="loading"
                  placeholder=" "
                  :maxlength="taxIDConfig.exactLength || taxIDConfig.maxLength"
                  :rules="taxIDRule"
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- ───────── ที่อยู่ ───────── -->
          <div class="form-section">
            <div class="section-header">
              <v-icon color="#6ec39b" class="tw-mr-2">mdi-map-marker-outline</v-icon>
              ที่อยู่
            </div>

            <v-row>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="addressObject.addressNo"
                  outlined
                  dense
                  hide-details="auto"
                  label="เลขที่"
                  :disabled="loading"
                  placeholder=" "
                  required
                  :rules="rules.inputRule"
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="addressObject.building"
                  outlined
                  dense
                  hide-details="auto"
                  label="อาคาร / ชั้น / ห้อง"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="addressObject.alley"
                  outlined
                  dense
                  hide-details="auto"
                  label="ตรอก / ซอย"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="addressObject.village"
                  outlined
                  dense
                  hide-details="auto"
                  label="หมู่ที่"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="addressObject.street"
                  outlined
                  dense
                  hide-details="auto"
                  label="ถนน"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3 col-auto-complete">
                <div class="material-textfield" :class="{ 'has-value': !!addressObject.postCode, 'has-error': alertZipcode, 'is-disabled': loading }">
                  <ThailandAutoComplete
                    v-model="addressObject.postCode"
                    type="zipcode"
                    size="large"
                    color="#6ec39b"
                    placeholder=" "
                    @select="select"
                  />
                  <label>รหัสไปรษณีย์</label>
                </div>
                <div v-if="alertZipcode" class="input-address-error">
                  กรุณากรอกข้อมูล
                </div>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3 col-auto-complete">
                <div class="material-textfield" :class="{ 'has-value': !!addressObject.district, 'has-error': alertAmphoe, 'is-disabled': loading }">
                  <ThailandAutoComplete
                    v-model="addressObject.district"
                    type="amphoe"
                    color="#6ec39b"
                    size="large"
                    placeholder=" "
                    @select="select"
                  />
                  <label>เขต / อำเภอ</label>
                </div>
                <div v-if="alertAmphoe" class="input-address-error">
                  กรุณากรอกข้อมูล
                </div>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3 col-auto-complete">
                <div class="material-textfield" :class="{ 'has-value': !!addressObject.subDistrict, 'has-error': alertDistrict, 'is-disabled': loading }">
                  <ThailandAutoComplete
                    v-model="addressObject.subDistrict"
                    type="district"
                    color="#6ec39b"
                    size="large"
                    placeholder=" "
                    @select="select"
                  />
                  <label>แขวง / ตำบล</label>
                </div>
                <div v-if="alertDistrict" class="input-address-error">
                  กรุณากรอกข้อมูล
                </div>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3 col-auto-complete">
                <div class="material-textfield" :class="{ 'has-value': !!addressObject.province, 'has-error': alertProvince, 'is-disabled': loading }">
                  <ThailandAutoComplete
                    v-model="addressObject.province"
                    type="province"
                    size="large"
                    color="#6ec39b"
                    placeholder=" "
                    @select="select"
                  />
                  <label>จังหวัด</label>
                </div>
                <div v-if="alertProvince" class="input-address-error">
                  กรุณากรอกข้อมูล
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- ───────── การติดต่อและอีเมล ───────── -->
          <div class="form-section">
            <div class="section-header">
              <v-icon color="#6ec39b" class="tw-mr-2">mdi-email-outline</v-icon>
              การติดต่อและอีเมล
            </div>

            <v-row>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.officeNumber"
                  outlined
                  dense
                  hide-details="auto"
                  label="เบอร์โทรศัพท์สำนักงาน"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.faxNumber"
                  outlined
                  dense
                  hide-details="auto"
                  label="เบอร์โทรสาร"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.phoneNumber"
                  outlined
                  dense
                  hide-details="auto"
                  label="เบอร์โทรศัพท์มือถือ"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.email"
                  outlined
                  dense
                  hide-details="auto"
                  label="อีเมล"
                  type="email"
                  :disabled="loading"
                  required
                  placeholder=" "
                  :rules="rules.inputRule"
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.emailSender"
                  outlined
                  dense
                  hide-details="auto"
                  label="อีเมลที่ใช้ส่งเอกสารให้ลูกค้า (ไม่จำเป็น)"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.emailSenderName"
                  outlined
                  dense
                  hide-details="auto"
                  label="ชื่อผู้ส่งอีเมล (ไม่จำเป็น)"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.emailDomain"
                  outlined
                  dense
                  hide-details="auto"
                  label="อีเมลโดเมน (ไม่จำเป็น)"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.emailAPIKey"
                  outlined
                  dense
                  hide-details="auto"
                  label="อีเมล API Key (ไม่จำเป็น)"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-text-field
                  v-model="companyObject.docPdfID"
                  outlined
                  dense
                  hide-details="auto"
                  label="Doc Pdf ID (ไม่จำเป็น)"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- ───────── Prefix เอกสาร ───────── -->
          <div class="form-section">
            <div class="section-header">
              <v-icon color="#6ec39b" class="tw-mr-2">mdi-tag-text-outline</v-icon>
              Prefix เอกสาร
            </div>

            <v-row>
              <v-col
                v-for="field in prefixFields"
                :key="field.key"
                cols="12"
                md="6"
                class="tw-py-3"
              >
                <v-text-field
                  v-model="prefixObectDetail[field.key]"
                  outlined
                  dense
                  hide-details="auto"
                  :label="field.label"
                  :disabled="loading"
                  placeholder=" "
                  color="#6ec39b"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row class="tw-mt-3">
              <v-col cols="12" class="tw-py-3">
                <div class="toggle-option-card">
                  <div>
                    <div class="tw-font-bold tw-text-slate-800">อัปโหลด 2 เทมเพลต</div>
                    <div class="tw-text-sm tw-text-slate-500">
                      สร้างเอกสารจากสองเทมเพลตพร้อมกัน
                    </div>
                  </div>
                  <v-switch
                    v-model="companyObject.uploadTemplate2File"
                    color="#6ec39b"
                    :disabled="loading"
                    hide-details
                    inset
                  ></v-switch>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- ───────── สถานะ & ประเภทบริษัท ───────── -->
          <div class="form-section">
            <div class="section-header">
              <v-icon color="#6ec39b" class="tw-mr-2">mdi-file-sign</v-icon>
              สถานะ &amp; ประเภทบริษัท
            </div>

            <v-row>
              <v-col cols="12" md="6" class="tw-py-3">
                <v-select
                  v-model="companyObject.environment"
                  :items="environmentItems"
                  item-text="text"
                  item-value="value"
                  label="ประเภทบริษัท (Environment)"
                  placeholder="ยังไม่กำหนด"
                  :hint="canEditEnvironment ? '' : 'เฉพาะ developer / engineer แก้ไขได้'"
                  persistent-hint
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  color="#6ec39b"
                  :disabled="loading || !canEditEnvironment"
                >
                  <template v-slot:item="{ item }">
                    <span class="env-dot" :style="{ backgroundColor: item.color }"></span>
                    {{ item.text }}
                  </template>
                  <template v-slot:selection="{ item }">
                    <span class="env-dot" :style="{ backgroundColor: item.color }"></span>
                    {{ item.text }}
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="6" class="tw-py-3 tw-flex tw-items-center">
                <v-switch
                  v-model="companyObject.showEnvironmentBadge"
                  color="#6ec39b"
                  :disabled="loading || !companyObject.environment || !canEditEnvironment"
                  hide-details
                  inset
                  class="tw-mt-0 tw-pt-0"
                >
                  <template v-slot:label>
                    <span class="tw-text-sm tw-text-slate-700">
                      แสดง badge ในหน้าต่างๆ
                    </span>
                  </template>
                </v-switch>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="tw-py-3">
                <div class="toggle-option-card hover-red">
                  <div>
                    <div class="tw-font-bold tw-text-slate-800">สิ้นสุดสัญญา</div>
                    <div class="tw-text-sm tw-text-slate-500">
                      บริษัทจะแสดงในรายการแบบสีเทา พร้อม badge "สิ้นสุดสัญญา"
                    </div>
                  </div>
                  <v-switch
                    v-model="companyObject.isInactive"
                    color="red"
                    :disabled="loading"
                    hide-details
                    inset
                  ></v-switch>
                </div>
              </v-col>
            </v-row>
          </div>

        </v-form>
      </v-card-text>

      <!-- Footer -->
      <v-card-actions class="tw-px-6 tw-py-4 tw-border-t dialog-footer">
        <v-spacer></v-spacer>
        <v-btn
          depressed
          :disabled="loading"
          class="cancel-btn tw-rounded-xl tw-text-base tw-font-semibold tw-px-5 tw-py-2"
          @click="onClickClose"
        >
          <v-icon left size="20">mdi-close-circle-outline</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          depressed
          :disabled="loading"
          :loading="loading"
          class="save-btn tw-rounded-xl tw-text-base tw-font-semibold tw-px-5 tw-py-2"
          @click="submit"
        >
          <v-icon left size="20">mdi-content-save-outline</v-icon>
          บันทึก
        </v-btn>
      </v-card-actions>
    </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
      right
    >
      <div class="tw-flex tw-items-center tw-gap-2">
        <v-icon color="white" size="20">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" aria-label="ปิด" @click="snackbar.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ThailandAutoComplete from "vue-thailand-address-autocomplete";
import { mapState } from "vuex";
import { checkImageSmall } from "../../../libs/validateImageUpload";
import fc from "@/firebaseConfig";
import {
  getCompanyCountries,
  getCountryConfig,
  validateTaxId,
  DEFAULT_COUNTRY_CODE,
} from "@/libs/companyCountries";
import {
  getCompanyEnvironments,
  getCompanyBadge,
  DEFAULT_ENVIRONMENT,
} from "@/libs/companyEnvironments";

const _ = require("lodash");

let addressObjectDetail = {
  addressNo: "",
  building: "",
  village: "",
  alley: "",
  street: "",
  province: "",
  district: "",
  subDistrict: "",
  postCode: "",
};

let companyObjectDetail = {
  nameTH: "",
  nameEN: "",
  countryCode: DEFAULT_COUNTRY_CODE,
  taxID: "",
  officeNumber: "",
  faxNumber: "",
  phoneNumber: "",
  email: "",
  docPdfID: "",
  imageLogoUrl: "",
  emailSender: "",
  emailSenderName: "",
  emailDomain: "",
  emailAPIKey: "",
  uploadTemplate2File: false,
  isInactive: false,
  environment: DEFAULT_ENVIRONMENT,
  showEnvironmentBadge: true,
};

let prefixObectDetail = {
  ab: [],
  tx: [],
  dr: [],
  cr: [],
  rt: [],
  re: [],
  cl: [],
};

const PREFIX_FIELDS = [
  { key: "rt", label: "Prefix ใบเสร็จรับเงิน/ใบกำกับภาษี" },
  { key: "tx", label: "Prefix ใบกำกับภาษีเต็มรูปแบบ" },
  { key: "dr", label: "Prefix ใบเพิ่มหนี้" },
  { key: "cr", label: "Prefix ใบลดหนี้" },
  { key: "re", label: "Prefix ใบรับ" },
  { key: "ab", label: "Prefix ใบกำกับภาษีอย่างย่อ" },
  { key: "cl", label: "Prefix ใบแจ้งยกเลิก" },
];

export default {
  name: "DialogEditCompany",
  components: {
    ThailandAutoComplete,
  },
  computed: {
    ...mapState({
      isOpenModal: (state) => state.companies.isOpenModalCompany,
      companyIDModal: (state) => state.companies.companyIDModal,
      companyDetail: (state) => state.companies.companyDetail,
      uploadCompanyStatus: (state) => state.companies.uploadCompanyStatus,
      uploadLogoState: (state) => state.companies.uploadLogoState,
      loading: (state) => state.companies.loadingUpdate,
      userCompany: (state) => state.user.userCompany,
      user: (state) => state.user.userData,
    }),
    // Only developers / engineers may change the environment or its badge.
    canEditEnvironment() {
      return !!(this.userCompany && this.userCompany.isDeveloper) ||
        !!(this.user && this.user.isEngineer);
    },
    countryItems() {
      return getCompanyCountries().map((c) => ({
        text: `${c.nameTH} (${c.nameEN})`,
        value: c.code,
      }));
    },
    environmentItems() {
      return getCompanyEnvironments().map((e) => ({
        text: e.label,
        value: e.value,
        color: e.color,
      }));
    },
    environmentBadge() {
      return getCompanyBadge(this.companyObject);
    },
    taxIDConfig() {
      return getCountryConfig(this.companyObject.countryCode).taxId;
    },
    taxIDLabel() {
      return this.taxIDConfig.exactLength
        ? `เลขประจำตัวผู้เสียภาษี ${this.taxIDConfig.exactLength} หลัก`
        : "เลขประจำตัวผู้เสียภาษี";
    },
    taxIDRule() {
      return [
        (v) => (!!v && this.isSetData) || "กรุณากรอกข้อมูล",
        (v) =>
          !this.isSetData || validateTaxId(v, this.companyObject.countryCode),
      ];
    },
  },
  data() {
    return {
      companyObject: _.cloneDeep(companyObjectDetail),
      addressObject: _.cloneDeep(addressObjectDetail),
      prefixObectDetail: _.cloneDeep(prefixObectDetail),
      prefixFields: PREFIX_FIELDS,
      alertProvince: false,
      alertAmphoe: false,
      alertDistrict: false,
      alertZipcode: false,
      isSetData: false,
      summaryAll: {},
      summaryUnsubscribe: null,
      snackbar: {
        show: false,
        text: "",
        color: "success",
        icon: "mdi-check-circle",
      },
      rules: {
        inputRule: [(v) => (!!v && this.isSetData) || "กรุณากรอกข้อมูล"],
      },
    };
  },
  methods: {
    getSummaryAll() {
      if (this.summaryUnsubscribe) {
        this.summaryUnsubscribe();
        this.summaryUnsubscribe = null;
      }
      this.summaryUnsubscribe = fc.companies
        .doc(this.companyIDModal)
        .collection("Summaries")
        .doc("all")
        .onSnapshot((allSummaries) => {
          this.summaryAll = allSummaries.data() || {};
        });
    },
    onFileChanged(event) {
      if (event) {
        checkImageSmall(event.target.files[0]).then((imageSmall) => {
          if (imageSmall) {
            alert("width lower 640 pixels. please select new image.");
          } else {
            let dataSet = _.cloneDeep(this.companyObject);
            dataSet.imageLogoUrl = event.target.files[0];
            dataSet.imageLogoShow = URL.createObjectURL(event.target.files[0]);
            this.companyObject = dataSet;
          }
        });
      }
    },
    select(address) {
      this.addressObject.subDistrict = address.district;
      this.addressObject.district = address.amphoe;
      this.addressObject.province = address.province;
      this.addressObject.postCode = address.zipcode;
    },
    submit: async function () {
      if (this.$refs.form && !this.$refs.form.validate()) return;

      let company = _.cloneDeep(this.companyObject);
      _.set(company, "address", this.addressObject);

      const prefixes = {};
      for (let field of this.prefixFields) {
        let value = this.prefixObectDetail[field.key];
        prefixes[field.key] =
          typeof value === "string" ? this.stringToArray(value) : value;
      }

      await this.changeInputDisableAutoComplete(true);
      await this.$store.dispatch("companies/setUpdateCompanyEmpty");
      await this.$store.dispatch("companies/updateCompany", {
        companyID: this.companyIDModal,
        company: company,
        prefixes: prefixes,
      });
    },
    changeInputDisableAutoComplete: async function (value) {
      let elements = await document.getElementsByClassName(
        "vth-addr-input vth-addr-input-size-large"
      );
      for (let i = 0; i < elements.length; i++) {
        if (value) elements[i].setAttribute("disabled", value);
        else elements[i].removeAttribute("disabled");
      }
    },
    setElement: async function () {
      let dialog = await document.getElementsByClassName(
        "v-dialog v-dialog--active"
      );
      if (dialog.length === 1) dialog[0].scrollTop = 0;
      let elements = await document.getElementsByClassName(
        "vth-addr-input vth-addr-input-size-large"
      );
      for (let i = 0; i < elements.length; i++) {
        elements[i].setAttribute("required", "required");
        if (i === 0) elements[i].setAttribute("id", "zipcode");
        if (i === 1) elements[i].setAttribute("id", "amphoe");
        if (i === 2) elements[i].setAttribute("id", "district");
        if (i === 3) elements[i].setAttribute("id", "province");
        elements[i].addEventListener("focusout", (event) => {
          if (event.target.value === "") {
            event.target.classList.add("input-error");
            event.target.parentNode.parentNode.nextElementSibling.classList.add(
              "text-color-error"
            );
            if (event.target.id === "province") {
              this.alertProvince = true;
            }
            if (event.target.id === "amphoe") {
              this.alertAmphoe = true;
            }
            if (event.target.id === "district") {
              this.alertDistrict = true;
            }
            if (event.target.id === "zipcode") {
              this.alertZipcode = true;
            }
          }
        });

        elements[i].addEventListener("keypress", () => {
          for (let i = 0; i < elements.length; i++) {
            elements[i].classList.remove("input-error");
            elements[
              i
            ].parentNode.parentNode.nextElementSibling.classList.remove(
              "text-color-error"
            );
            this.alertProvince = false;
            this.alertAmphoe = false;
            this.alertDistrict = false;
            this.alertZipcode = false;
          }
        });
      }
    },
    setDataCompany: function () {
      if (!this.companyDetail) return;
      let companyObjectData = _.cloneDeep(this.companyObject);
      for (let keysKey of _.keys(this.companyObject)) {
        let value = _.get(this.companyDetail, keysKey);
        // รองรับ boolean (true/false) และค่าที่ไม่ใช่ null/undefined
        if (value !== undefined && value !== null) {
          _.set(companyObjectData, keysKey, value);
        }
      }
      this.companyObject = companyObjectData;

      let addressObjectData = _.cloneDeep(this.addressObject);
      for (let keysKey of _.keys(this.addressObject)) {
        if (
          this.companyDetail.address &&
          !_.isEmpty(this.companyDetail.address[keysKey])
        ) {
          _.set(
            addressObjectData,
            keysKey,
            _.get(this.companyDetail.address, keysKey)
          );
        }
      }
      this.addressObject = addressObjectData;
      this.isSetData = true;
    },
    onClickClose: function () {
      this.$store.dispatch("companies/setCloseModalCompany");
    },
    clearStateEmpty: function () {
      if (this.summaryUnsubscribe) {
        this.summaryUnsubscribe();
        this.summaryUnsubscribe = null;
      }
      this.companyObject = _.cloneDeep(companyObjectDetail);
      this.addressObject = _.cloneDeep(addressObjectDetail);
      this.prefixObectDetail = _.cloneDeep(prefixObectDetail);
      this.alertProvince = false;
      this.alertAmphoe = false;
      this.alertDistrict = false;
      this.alertZipcode = false;
      this.isSetData = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    stringToArray(_input) {
      if (typeof _input !== "string") return _input;
      const trimmed = _input.trim();
      if (!trimmed) return [];
      return trimmed
        .split(/,\s*/)
        .map((item) => item.trim())
        .filter(Boolean);
    },
  },
  watch: {
    isOpenModal: {
      handler: async function () {
        if (this.isOpenModal) {
          await this.setElement();
          await this.$store.dispatch("companies/getCompany", {
            companyID: this.companyIDModal,
          });
          this.getSummaryAll();
        } else {
          this.clearStateEmpty();
          await this.$store.dispatch("companies/getList");
        }
      },
    },
    summaryAll: {
      handler: function () {
        this.prefixObectDetail = {
          ...prefixObectDetail,
          ...(this.summaryAll && this.summaryAll.prefixes),
        };
      },
    },
    companyDetail: {
      handler: function () {
        this.setDataCompany();
      },
    },
    uploadCompanyStatus: {
      handler: function () {
        if (!this.uploadCompanyStatus) return;
        if (this.uploadCompanyStatus === "success") {
          this.changeInputDisableAutoComplete();
          this.snackbar = {
            show: true,
            text: "บันทึกข้อมูลบริษัทสำเร็จ",
            color: "success",
            icon: "mdi-check-circle",
          };
        } else if (this.uploadCompanyStatus === "error") {
          this.snackbar = {
            show: true,
            text: "ไม่สามารถบันทึกได้ กรุณาลองใหม่อีกครั้ง",
            color: "error",
            icon: "mdi-alert-circle",
          };
        }
      },
    },
  },
  beforeDestroy() {
    if (this.summaryUnsubscribe) {
      this.summaryUnsubscribe();
      this.summaryUnsubscribe = null;
    }
  },
};
</script>

<style lang="scss">
.alert-detail {
  padding-left: 20px;
  padding-right: 20px;
}

#edit-company {
  .dialog-header {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fff;
    border-bottom: 1px solid #f1f5f9 !important;
  }

  .dialog-footer {
    background: #fff;
    border-top: 1px solid #f1f5f9 !important;
  }

  .form-section {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #6ec39b; /* Brand Accent */
    border-radius: 12px;
    padding: 20px 22px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.25s ease, transform 0.25s ease;

    &:hover {
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.08);
    }
  }

  .section-header {
    display: flex;
    align-items: center;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1e293b; /* slate-800 */
    margin-bottom: 16px;
    letter-spacing: -0.01em;
  }

  .v-input {
    border-radius: 10px;
  }

  .v-card__text {
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f8fafc;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;
      
      &:hover {
        background: #94a3b8;
      }
    }
  }
}

.logo-uploader {
  position: relative;
  flex: 0 0 auto;
  width: 84px;
  height: 84px;
  border-radius: 16px;
  border: dashed 2px #cbd5e1;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #6ec39b;
    background-color: #f8fafc;
  }

  .logo-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    
    .logo-placeholder-text {
      font-size: 11px;
      font-weight: 600;
      color: #94a3b8;
    }
  }

  .logo-preview-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .logo-overlay {
      position: absolute;
      inset: 0;
      background-color: rgba(15, 23, 42, 0.6);
      backdrop-filter: blur(2px);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      opacity: 0;
      transition: opacity 0.2s ease;

      .logo-overlay-text {
        font-size: 10px;
        font-weight: 600;
        color: #ffffff;
      }
    }

    &:hover .logo-overlay {
      opacity: 1;
    }
  }

  .logo-progress {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

.material-textfield {
  position: relative;
  width: 100%;

  .vth-addr-container {
    width: 100%;
  }

  .vth-addr-input {
    width: 100% !important;
    height: 40px !important;
    min-height: 40px !important;
    font-size: 14px !important;
    padding: 8px 12px !important;
    border-radius: 10px !important;
    border: 1px solid rgba(0, 0, 0, 0.24) !important;
    background-color: transparent !important;
    color: rgba(0, 0, 0, 0.87) !important;
    transition: border-color 0.2s ease, border-width 0.1s ease;
    box-shadow: none !important;
    box-sizing: border-box !important;

    &:hover {
      border-color: rgba(0, 0, 0, 0.87) !important;
    }

    &:focus {
      border-color: #6ec39b !important;
      border-width: 2px !important;
      outline: none !important;
    }

    &.input-error {
      border-color: #b71c1c !important;
      border-width: 2px !important;
    }

    &[disabled] {
      background-color: #f1f5f9 !important; /* slate-100 */
      border-color: #e2e8f0 !important;
      color: #94a3b8 !important;
      cursor: not-allowed !important;
      
      &:hover {
        border-color: #e2e8f0 !important;
      }
    }
  }

  label {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    background-color: transparent;
    color: rgba(0, 0, 0, 0.54);
    padding: 0 4px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    font-size: 14px;
    z-index: 2;
    transform-origin: left top;

    &.text-color-error {
      color: #b71c1c !important;
    }
  }

  /* Floating state when focused or having a value */
  &:focus-within label,
  &.has-value label {
    top: 0;
    transform: translateY(-50%) scale(0.85);
    background-color: white;
    color: #6ec39b;
  }

  &.has-error label {
    color: #b71c1c;
  }

  &.is-disabled label {
    color: #94a3b8;
    background-color: transparent;
  }
}

/* Autocomplete cols use the same `tw-py-3` rhythm as the Vuetify fields;
   the error message is positioned absolutely so it never shifts the grid. */
.col-auto-complete {
  position: relative;
}

.env-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: middle;
  flex: 0 0 auto;
}

.input-address-error {
  position: absolute;
  bottom: -2px;
  left: 12px;
  font-size: 12px;
  color: #b71c1c;
  line-height: 1.2;
  font-weight: 500;
}

.vth-addr-autocomplete {
  border-radius: 12px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1) !important;
  background-color: #ffffff !important;
  z-index: 9999 !important;
  max-height: 200px !important;
  overflow-y: auto !important;
  padding: 4px !important;

  li {
    padding: 8px 12px !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    color: #334155 !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;

    &:hover, &.is-active {
      background-color: #f1f5f9 !important;
      color: #0f172a !important;
    }
  }
}

.close-btn {
  transition: background-color 0.2s ease, transform 0.2s ease !important;

  &:hover {
    background-color: #f1f5f9 !important;
    transform: rotate(90deg);
  }
}

.cancel-btn {
  background-color: transparent !important;
  color: #64748b !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f1f5f9 !important;
  }
}

.save-btn {
  background-color: #6ec39b !important;
  color: #ffffff !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: 0 4px 6px -1px rgba(110, 195, 155, 0.2), 0 2px 4px -2px rgba(110, 195, 155, 0.2) !important;
  transition: all 0.2s ease;

  &:hover {
    background-color: #5db28a !important;
    box-shadow: 0 10px 15px -3px rgba(110, 195, 155, 0.3), 0 4px 6px -4px rgba(110, 195, 155, 0.3) !important;
  }
}

.toggle-option-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    background-color: #f1f5f9;
  }

  &.hover-red:hover {
    border-color: #fca5a5;
    background-color: #fef2f2;
  }

  .v-input--switch {
    margin-top: 0;
    padding-top: 0;
  }
}
</style>
